import { ConfigurationSelector } from '@ds-mgnl/marketing';
import { useMemo } from 'react';
import type { AssetDimension } from '../../../services/productApi';
import { chipUrl } from '../chipUrl';
import styles from '../FamilyVariantPicker/FamilyVariantPicker.module.css';

export interface FabricPreviewPickerProps {
	dimension: AssetDimension;
	selectedValueSlug: string | undefined;
	loading?: boolean;
	onSelect: (valueSlug: string) => void;
	/**
	 * Overrides the selector label (defaults to the dimension's display). Used to
	 * render a second picker off the same fabric dimension as "Back Fabric".
	 */
	selectorLabel?: string;
}

export function FabricPreviewPicker({
	dimension,
	selectedValueSlug,
	loading = false,
	onSelect,
	selectorLabel,
}: FabricPreviewPickerProps) {
	const values = useMemo(
		() =>
			Object.entries(dimension.values).map(([slug, value]) => ({
				slug,
				display: value.display,
				chipUrl: chipUrl(value),
			})),
		[dimension],
	);

	const items = values.map((value) => ({
		id: value.slug,
		label: value.display,
		imageProps: value.chipUrl
			? // crossOrigin forces the thumbnail into cors mode so it shares the
			  // same cache entry as the model-viewer's texture fetch for this
			  // presigned URL. Cast past DSX's AcceptedImgAttributes whitelist;
			  // the attr is spread onto the underlying <img> at runtime.
			  ({
					src: value.chipUrl,
					alt: value.display,
					crossOrigin: 'anonymous',
				} as { src: string; alt: string })
			: undefined,
	}));

	if (values.length === 0) return null;

	return (
		<div className={styles['picker']}>
			<ConfigurationSelector
				selectorType={selectorLabel ?? dimension.display}
				items={items}
				value={selectedValueSlug}
				loading={loading}
				onChange={(nextSlug: string) => onSelect(nextSlug)}
			/>
		</div>
	);
}
